/**
 * O que a Venda na Rota precisa do APP que a hospeda (ERP Web ou CaféGestor Vendedor) — A0.
 *
 * O pacote não conhece o login, o cadastro de permissões nem a tela de acompanhamento da nota
 * de cada app: recebe tudo por aqui. Assim a mesma venda roda no navegador (Web) e no Android
 * (Vendedor), cada um com a sua sessão, a sua forma de mostrar a NF-e e — a partir da A3 — a
 * sua impressão. As permissões aqui só ESCONDEM botões: quem decide é a API.
 */
import type { PermissionCode, PrintLayout } from "@cafegestor/shared";
import type { Transport } from "@cafegestor/client/printing";
import { createContext, useContext, type ComponentType } from "react";

/** Como o app imprime ou compartilha o Pedido de Venda (padrão: transportes do navegador). */
export type RotaPrinter = {
  /** meios oferecidos na tela, em ordem de preferência */
  options: Transport[];
  /** envia o layout pelo meio escolhido; lança erro se falhar (a venda não muda) */
  print: (layout: PrintLayout, transport: Transport) => Promise<void>;
};

export type RotaHost = {
  /** usuário logado (dono das vendas guardadas no aparelho); null sem sessão */
  userId: string | null;
  /** nome da empresa ativa (cabeçalho do Pedido de Venda impresso) */
  companyName: string;
  /** permissão na empresa ativa (só para esconder o que não se pode fazer) */
  can: (code: PermissionCode) => boolean;
  /** acompanhamento da nota de um pedido (cada app mostra do seu jeito) */
  EmissionTracking: ComponentType<{ documentId: string; since: string }>;
  /**
   * Venda sem conexão (padrão: true, como na R2 — a venda fica guardada e é enviada depois).
   * O CaféGestor Vendedor V1 usa false: a venda só é confirmada com conexão (o offline completo,
   * com armazenamento criptografado, vem na V3).
   */
  offlineSales?: boolean;
  /**
   * Onde ficam as cópias das listas (clientes, produtos...) e o rascunho da venda. Padrão:
   * "aparelho" (localStorage/sessionStorage do navegador, como na R2). "memoria": nada de dado
   * de cliente fica gravado sem proteção — só enquanto o app está aberto (V1 do app).
   */
  deviceData?: "aparelho" | "memoria";
  /** Impressão e compartilhamento do Pedido de Venda (padrão: transportes do navegador). */
  printer?: RotaPrinter;
};

export const RotaHostContext = createContext<RotaHost | null>(null);

/** O app que hospeda a Venda na Rota (falha cedo e claro se faltar o RotaHostProvider). */
export function useRotaHost(): RotaHost {
  const host = useContext(RotaHostContext);
  if (!host)
    throw new Error("Venda na Rota sem RotaHostProvider: o app precisa informar a sessão.");
  return host;
}
